import {
  ExternalLink,
  ImageUp,
  Loader2,
  Trash2,
  UploadCloud,
} from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Constants } from "src/constant";
import { actorFromAuth } from "src/lib/activityService";
import {
  clampExpiryDays,
  hostedImageShareUrl,
} from "src/lib/imageHostingService";
import {
  canManageService,
  canWriteService,
  ServiceDomain,
} from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { useImageHostingStore } from "src/store/imageHostingStore";
import type { HostedImageListItem } from "src/types/hostedImage";
import { useShallow } from "zustand/react/shallow";
import CopyButton from "../components/CopyButton";
import { ImageDropzone } from "../components/HappeningNow/ImageDropzone";
import { Input } from "../components/ui/Input";
import { SlabButton } from "../components/ui/SlabButton";
import { ViewOnlyNotice } from "../components/ViewOnlyNotice";

const teal = { primary: "#14B8A6", shadow: "#0F766E" } as const;

const labelClass =
  "flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]";

const EXPIRY_PRESETS = [30, 90, 180];

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** "3 days left" / "Expired" from an expiry timestamp. */
function expiryLabel(ts: HostedImageListItem["expiresAt"]): {
  text: string;
  expired: boolean;
} {
  const ms = ts.toMillis() - Date.now();
  if (ms <= 0) return { text: "Expired", expired: true };
  const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
  return {
    text: `${days} ${days === 1 ? "day" : "days"} left`,
    expired: false,
  };
}

const ImageHostingPanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const actor = actorFromAuth(user, userProfile);
  const canUpload =
    userProfile != null &&
    canWriteService(userProfile.role, ServiceDomain.ImageHosting);
  const canDelete =
    userProfile != null &&
    canManageService(userProfile.role, ServiceDomain.ImageHosting);
  const {
    items,
    loading,
    uploading,
    error,
    success,
    busyId,
    file,
    fileError,
    expiryDays,
    setFile,
    setFileError,
    setExpiryDays,
    clearMessages,
    load,
    upload,
    remove,
  } = useImageHostingStore(
    useShallow((s) => ({
      items: s.items,
      loading: s.loading,
      uploading: s.uploading,
      error: s.error,
      success: s.success,
      busyId: s.busyId,
      file: s.file,
      fileError: s.fileError,
      expiryDays: s.expiryDays,
      setFile: s.setFile,
      setFileError: s.setFileError,
      setExpiryDays: s.setExpiryDays,
      clearMessages: s.clearMessages,
      load: s.load,
      upload: s.upload,
      remove: s.remove,
    })),
  );

  const uid = user?.uid ?? null;

  const ownerDisplayName = useMemo(
    () =>
      userProfile?.displayName?.trim() ||
      user?.displayName?.trim() ||
      user?.email ||
      user?.uid ||
      "",
    [userProfile, user],
  );

  useEffect(() => {
    if (uid) void load(uid);
  }, [uid, load]);

  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => clearMessages(), 5000);
    return () => window.clearTimeout(id);
  }, [success, clearMessages]);

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid) return;
    void upload(uid, ownerDisplayName, actor);
  };

  return (
    <div
      className="mx-auto max-w-3xl pb-10 pt-2 sm:max-w-4xl sm:pt-4"
      style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
    >
      <header className="mb-6 flex items-center gap-3">
        <span
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-[0_4px_0_#0F766E]"
          style={{ backgroundColor: teal.primary }}
        >
          <ImageUp className="h-6 w-6" strokeWidth={2.5} aria-hidden />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-3xl">
            {Constants.IMAGE_HOSTING_TITLE}
          </h1>
          <p className="text-sm font-bold text-white/80 sm:text-base">
            {Constants.IMAGE_HOSTING_DESCRIPTION}
          </p>
        </div>
      </header>

      {error && (
        <div className="mb-6 rounded-2xl border-2 border-[#EF4444] bg-[#FEE2E2] p-4">
          <p className="text-center text-sm font-extrabold text-[#B91C1C]">
            {error}
          </p>
        </div>
      )}
      {success && (
        <div className="mb-6 rounded-2xl border-2 border-[#22C55E] bg-[#DCFCE7] p-4">
          <p className="text-center text-sm font-extrabold text-[#166534]">
            {success}
          </p>
        </div>
      )}
      {!canUpload && (
        <ViewOnlyNotice
          title="View only"
          description="Your role can't upload images."
        />
      )}
      {/* Upload card */}
      <section
        className="rounded-[1.75rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-6"
        aria-labelledby="image-hosting-upload-heading"
      >
        <h2
          id="image-hosting-upload-heading"
          className="mb-4 text-lg font-extrabold text-[#3C3C3C] sm:text-xl"
        >
          Upload an image
        </h2>

        <form onSubmit={handleUpload} className="flex flex-col gap-5">
          <ImageDropzone
            file={file}
            onFileChange={setFile}
            onError={setFileError}
            required
            maxBytes={Constants.IMAGE_HOSTING_MAX_BYTES}
            hint={Constants.IMAGE_HOSTING_UPLOAD_HINT}
          />
          {fileError && (
            <p className="rounded-xl bg-[#FEE2E2] px-4 py-2 text-center text-sm font-extrabold text-[#B91C1C]">
              {fileError}
            </p>
          )}

          <label className={labelClass}>
            Link expires after
            <div className="flex flex-wrap items-center gap-2">
              {EXPIRY_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setExpiryDays(preset)}
                  className={[
                    "min-h-[44px] rounded-2xl border-2 px-4 text-sm font-extrabold transition-all active:translate-y-0.5",
                    expiryDays === preset
                      ? "border-[#0F766E] bg-[#14B8A6] text-white shadow-[0_3px_0_#0F766E]"
                      : "border-[#E5E5E5] bg-white text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0] hover:bg-[#F7F7F7]",
                  ].join(" ")}
                >
                  {preset} days
                </button>
              ))}
              <span className="flex items-center gap-2 text-xs font-bold normal-case tracking-normal text-[#777777]">
                or
                <Input
                  accent="teal"
                  type="number"
                  min={1}
                  max={Constants.IMAGE_HOSTING_MAX_EXPIRY_DAYS}
                  value={expiryDays}
                  onChange={(e) =>
                    setExpiryDays(clampExpiryDays(Number(e.target.value)))
                  }
                  aria-label="Custom expiry in days"
                  className="w-24"
                />
                days
              </span>
            </div>
          </label>

          <SlabButton
            variant="primary"
            type="submit"
            disabled={uploading || !canUpload}
            backgroundColor={teal.primary}
            lipColor={teal.shadow}
            shadowLiftPx={6}
            shadowLiftActivePx={2}
            className="w-full px-6 text-base disabled:opacity-60"
          >
            {uploading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                Uploading…
              </>
            ) : (
              <>
                <UploadCloud className="h-5 w-5" aria-hidden />
                Upload &amp; get link
              </>
            )}
          </SlabButton>
        </form>
      </section>

      {/* Uploads list */}
      <section aria-labelledby="image-hosting-list-heading" className="mt-8">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2
            id="image-hosting-list-heading"
            className="text-base font-extrabold text-white sm:text-lg"
          >
            Your uploads
          </h2>
          {!loading && items.length > 0 && (
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-extrabold text-white/90 ring-2 ring-white/20">
              {items.length} {items.length === 1 ? "image" : "images"}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center rounded-2xl border-4 border-white/10 bg-white/5 py-10">
            <Loader2 className="h-8 w-8 animate-spin text-[#14B8A6]" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border-4 border-dashed border-white/25 bg-white/5 p-6 text-center">
            <p className="text-base font-extrabold text-white/90">
              No uploads yet
            </p>
            <p className="mt-1 text-sm font-bold text-white/60">
              Upload an image above to get a shareable link.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((item) => {
              const exp = expiryLabel(item.expiresAt);
              const shareUrl = hostedImageShareUrl(item);
              return (
                <li
                  key={item.id}
                  className="flex items-start gap-3 rounded-2xl border-2 border-[#E5E5E5] bg-white p-3 shadow-[0_4px_0_#D0D0D0] sm:p-4"
                >
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 border-[#E5E5E5] bg-[#F7F7F7]">
                    <img
                      src={item.downloadUrl}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-extrabold text-[#3C3C3C]">
                        {item.fileName}
                      </p>
                      <span
                        className={[
                          "rounded-lg px-2 py-0.5 text-[10px] font-extrabold uppercase",
                          exp.expired
                            ? "bg-[#FEE2E2] text-[#B91C1C]"
                            : "bg-[#ECFDF5] text-[#0F766E]",
                        ].join(" ")}
                      >
                        {exp.text}
                      </span>
                      <span className="text-[11px] font-bold text-[#9CA3AF]">
                        {formatBytes(item.sizeBytes)}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-2 rounded-xl bg-[#F7F7F7] px-2 py-1.5">
                      <a
                        href={shareUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex min-w-0 flex-1 items-center gap-1 truncate font-mono text-xs font-bold text-[#6366F1] hover:underline"
                        title={shareUrl}
                      >
                        <ExternalLink
                          className="h-3 w-3 shrink-0"
                          aria-hidden
                        />
                        <span className="truncate">{shareUrl}</span>
                      </a>
                      <CopyButton value={shareUrl} />
                    </div>
                  </div>

                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => void remove(item, actor)}
                      disabled={busyId === item.id}
                      aria-label={`Delete ${item.fileName}`}
                      title="Delete"
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-2 border-[#FF4B4B] bg-[#FFE0E0] text-[#B32626] shadow-[0_3px_0_#D93636] transition-all active:translate-y-0.5 disabled:opacity-50"
                    >
                      {busyId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Trash2 className="h-4 w-4" aria-hidden />
                      )}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default ImageHostingPanel;
